// Apex-proxy snippet templates. Each customer picks the host they run their
// main site on; we render the corresponding config block that rewrites the
// enabled well-known paths to fetch from their SitemapHost subdomain.
//
// IMPORTANT: every snippet uses a rewrite (transparent, 200) — never a 301/302
// redirect — so the URL stays at the apex and agents see the file at the
// canonical path they expect (per RFC 9309 origin scoping).
//
// See strategy.md §2 for the design context. See comms-plan.md §3.1 for
// the customer-facing docs page that explains this in prose.

export type ProxyHostKind =
  | 'vercel'
  | 'netlify'
  | 'cloudflare-pages'
  | 'cloudflare-workers'
  | 'nextjs'
  | 'nginx'
  | 'apache'
  | 'caddy'
  | 'cloudfront'
  | 'generic';

export interface ProxyHostInfo {
  kind: ProxyHostKind;
  label: string;
  description: string;
  filename: string; // The file the customer pastes the snippet into
  language: 'json' | 'toml' | 'text' | 'javascript' | 'typescript' | 'nginx' | 'apache' | 'caddyfile';
}

export const PROXY_HOSTS: Record<ProxyHostKind, ProxyHostInfo> = {
  vercel: {
    kind: 'vercel',
    label: 'Vercel',
    description: 'vercel.json rewrites — works on every Vercel plan',
    filename: 'vercel.json',
    language: 'json',
  },
  netlify: {
    kind: 'netlify',
    label: 'Netlify',
    description: '_redirects with 200 status (rewrite, not redirect)',
    filename: '_redirects',
    language: 'text',
  },
  'cloudflare-pages': {
    kind: 'cloudflare-pages',
    label: 'Cloudflare Pages',
    description: '_redirects (Netlify-compatible) at the project root',
    filename: '_redirects',
    language: 'text',
  },
  'cloudflare-workers': {
    kind: 'cloudflare-workers',
    label: 'Cloudflare Workers',
    description: 'Workers fetch handler — for sites already served by a Worker',
    filename: 'src/index.ts (or your existing Worker)',
    language: 'typescript',
  },
  nextjs: {
    kind: 'nextjs',
    label: 'Next.js (self-hosted)',
    description: 'next.config.js async rewrites() — works on Vercel too',
    filename: 'next.config.js',
    language: 'javascript',
  },
  nginx: {
    kind: 'nginx',
    label: 'nginx',
    description: 'location blocks with proxy_pass — paste into your server block',
    filename: 'nginx.conf or sites-available/<your-site>',
    language: 'nginx',
  },
  apache: {
    kind: 'apache',
    label: 'Apache (mod_rewrite + mod_proxy)',
    description: 'RewriteRule with the [P] flag — requires mod_proxy enabled',
    filename: '.htaccess or VirtualHost config',
    language: 'apache',
  },
  caddy: {
    kind: 'caddy',
    label: 'Caddy',
    description: 'reverse_proxy directives — paste into your site block',
    filename: 'Caddyfile',
    language: 'caddyfile',
  },
  cloudfront: {
    kind: 'cloudfront',
    label: 'AWS CloudFront',
    description: 'CloudFront origin + behavior config — see notes below',
    filename: 'CloudFront distribution config',
    language: 'text',
  },
  generic: {
    kind: 'generic',
    label: 'Other / generic',
    description: 'Plain-language description for any reverse-proxy-capable host',
    filename: 'your host config',
    language: 'text',
  },
};

// The full set of well-known paths SitemapHost can serve on the subdomain.
// `defaultOn` controls which ones are enabled by default in the dashboard.
// robots.txt is OFF by default because enabling it overrides whatever the
// customer already publishes — most customers want to keep their own.
export interface ProxyPath {
  path: string;
  label: string;
  defaultOn: boolean;
  description: string;
}

export const PROXY_PATHS: ProxyPath[] = [
  {
    path: '/llms.txt',
    label: 'llms.txt',
    defaultOn: true,
    description: 'Markdown summary for LLMs and IDE agents.',
  },
  {
    path: '/llms-full.txt',
    label: 'llms-full.txt',
    defaultOn: true,
    description: 'Extended markdown content for LLMs.',
  },
  {
    path: '/.well-known/',
    label: '/.well-known/*',
    defaultOn: true,
    description: 'Agent discovery surfaces (MCP Server Card, A2A Agent Card, agents.json, agents.txt, etc.).',
  },
  {
    path: '/robots.txt',
    label: 'robots.txt',
    defaultOn: false,
    description: 'Replaces your site\'s existing robots.txt with the one SitemapHost manages. Most customers leave this off and only add a Sitemap: directive to their own robots.txt.',
  },
];

// Render the snippet for a given host. Each renderer produces the exact text
// the customer copies into the file named by ProxyHostInfo.filename.
export function renderSnippet(
  kind: ProxyHostKind,
  subdomain: string,
  enabledPaths: string[]
): string {
  if (enabledPaths.length === 0) {
    return '# No paths selected. Enable at least one path above to render a snippet.';
  }
  switch (kind) {
    case 'vercel':
      return renderVercel(subdomain, enabledPaths);
    case 'netlify':
    case 'cloudflare-pages':
      return renderRedirectsFile(subdomain, enabledPaths);
    case 'cloudflare-workers':
      return renderCloudflareWorker(subdomain, enabledPaths);
    case 'nextjs':
      return renderNextJs(subdomain, enabledPaths);
    case 'nginx':
      return renderNginx(subdomain, enabledPaths);
    case 'apache':
      return renderApache(subdomain, enabledPaths);
    case 'caddy':
      return renderCaddy(subdomain, enabledPaths);
    case 'cloudfront':
      return renderCloudFront(subdomain, enabledPaths);
    case 'generic':
      return renderGeneric(subdomain, enabledPaths);
  }
}

function renderVercel(subdomain: string, enabledPaths: string[]): string {
  const rewrites = enabledPaths.map((p) => {
    if (p.endsWith('/')) {
      return {
        source: `${p}:path*`,
        destination: `https://${subdomain}${p}:path*`,
      };
    }
    return { source: p, destination: `https://${subdomain}${p}` };
  });
  return JSON.stringify({ rewrites }, null, 2);
}

function renderRedirectsFile(subdomain: string, enabledPaths: string[]): string {
  // Netlify _redirects + Cloudflare Pages _redirects share the same syntax.
  // The trailing `200!` forces a rewrite (transparent) instead of a 301.
  return enabledPaths
    .map((p) => {
      if (p.endsWith('/')) {
        return `${p}*  https://${subdomain}${p}:splat  200!`;
      }
      return `${p}  https://${subdomain}${p}  200!`;
    })
    .join('\n');
}

function renderCloudflareWorker(subdomain: string, enabledPaths: string[]): string {
  const checks = enabledPaths
    .map((p) => {
      if (p.endsWith('/')) {
        return `    if (url.pathname.startsWith(${JSON.stringify(p)})) {
      return fetch(\`https://${subdomain}\${url.pathname}\${url.search}\`, request);
    }`;
      }
      return `    if (url.pathname === ${JSON.stringify(p)}) {
      return fetch(\`https://${subdomain}\${url.pathname}\${url.search}\`, request);
    }`;
    })
    .join('\n');
  return `// Drop this block at the TOP of your Worker's fetch handler — before
// your existing routing logic.
export default {
  async fetch(request: Request): Promise<Response> {
    const url = new URL(request.url);
${checks}

    // ... your existing Worker logic continues below
    return new Response('Not found', { status: 404 });
  },
};`;
}

function renderNextJs(subdomain: string, enabledPaths: string[]): string {
  const items = enabledPaths.map((p) => {
    if (p.endsWith('/')) {
      return `      { source: '${p}:path*', destination: 'https://${subdomain}${p}:path*' }`;
    }
    return `      { source: '${p}', destination: 'https://${subdomain}${p}' }`;
  });
  return `// next.config.js — merge with any existing rewrites you have.
module.exports = {
  async rewrites() {
    return [
${items.join(',\n')},
    ];
  },
};`;
}

function renderNginx(subdomain: string, enabledPaths: string[]): string {
  const blocks = enabledPaths.map((p) => {
    if (p.endsWith('/')) {
      // location with trailing slash + proxy_pass keeps the path suffix
      return `  location ${p} {
    proxy_pass https://${subdomain}${p};
    proxy_set_header Host ${subdomain};
  }`;
    }
    // exact-match
    return `  location = ${p} {
    proxy_pass https://${subdomain}${p};
    proxy_set_header Host ${subdomain};
  }`;
  });
  return `# Paste these blocks inside the server { } block for your site.
# Requires nginx with HTTPS upstream support (default on recent builds).
${blocks.join('\n\n')}`;
}

function renderApache(subdomain: string, enabledPaths: string[]): string {
  const rules = enabledPaths.map((p) => {
    if (p.endsWith('/')) {
      // Rewrite a path prefix
      const escaped = p.replace(/\./g, '\\.');
      return `RewriteRule ^${escaped.slice(1)}(.*)$ https://${subdomain}${p}$1 [P,L]`;
    }
    const escaped = p.replace(/\./g, '\\.');
    return `RewriteRule ^${escaped.slice(1)}$ https://${subdomain}${p} [P,L]`;
  });
  return `# .htaccess or inside <VirtualHost>. Requires mod_proxy + mod_proxy_http
# + mod_rewrite — and SSLProxyEngine on for HTTPS upstream.
RewriteEngine On
SSLProxyEngine on
${rules.join('\n')}`;
}

function renderCaddy(subdomain: string, enabledPaths: string[]): string {
  const matchers = enabledPaths.map((p) => {
    if (p.endsWith('/')) {
      return `  reverse_proxy ${p}* https://${subdomain} {
    header_up Host ${subdomain}
  }`;
    }
    return `  reverse_proxy ${p} https://${subdomain} {
    header_up Host ${subdomain}
  }`;
  });
  return `# Caddyfile — paste inside your site's block (the one with your domain
# name and tls config). Caddy handles HTTPS upstream automatically.
${matchers.join('\n\n')}`;
}

function renderCloudFront(subdomain: string, enabledPaths: string[]): string {
  return `# CloudFront does this via the console, not a file. Steps:
#
# 1. In your CloudFront distribution, add a new ORIGIN:
#      Origin domain: ${subdomain}
#      Protocol:      HTTPS only
#      Name:          sitemaphost-apex-proxy
#
# 2. For EACH of these path patterns, add a BEHAVIOR pointing at that origin:
${enabledPaths.map((p) => `#      - ${p.endsWith('/') ? `${p}*` : p}`).join('\n')}
#
# 3. In each behavior:
#      - Viewer protocol policy: Redirect HTTP to HTTPS (or HTTPS only)
#      - Allowed methods:        GET, HEAD
#      - Cache policy:           CachingOptimized (or your default)
#      - Origin request policy:  AllViewerExceptHostHeader
#
# 4. Deploy the distribution (5–10 min propagation).
#
# Once live, https://<your-apex>/llms.txt will serve from SitemapHost
# without a URL change.`;
}

function renderGeneric(subdomain: string, enabledPaths: string[]): string {
  return `# Generic instructions for any reverse-proxy-capable host.
#
# For each path below, configure your host to fetch the response from the
# SitemapHost subdomain WITHOUT changing the URL the client sees. The
# customer's browser/agent must keep seeing https://<your-apex>/<path> in
# the URL bar — internally, your host fetches https://${subdomain}/<path>.
#
# This is a REWRITE (HTTP 200 with proxied body), not a REDIRECT (HTTP 301/302).
# Redirects break the spec — agents must see the file at the apex URL.
#
# Paths to proxy:
${enabledPaths.map((p) => `#   ${p.endsWith('/') ? `${p}*` : p}   →   https://${subdomain}${p.endsWith('/') ? `${p}*` : p}`).join('\n')}
#
# If your host doesn't support reverse proxy, see the Cloudflare Workers
# template for an out-of-band option you can drop in front of your existing
# site without changing your origin.`;
}

export function parseEnabledPaths(json: string | null): string[] {
  if (!json) {
    return PROXY_PATHS.filter((p) => p.defaultOn).map((p) => p.path);
  }
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is string => typeof s === 'string');
  } catch {
    return [];
  }
}

export function serializeEnabledPaths(paths: string[]): string {
  return JSON.stringify(paths);
}
